import React, { useState } from 'react';
import { tokenize, displayUrl, formatRelative, truncatePost, clampAspect } from '../utils/xFormat';

// Um registro do building in public no formato de post do X: avatar, nome,
// texto com "Mostrar mais", mídia, post citado e, se for thread, o resto dela
// expandindo no lugar. Métricas de engajamento ficam de fora de propósito
// (mesma decisão do adverse.com.br/building-in-public).
//
// No site ele é o corpo do XPostModal, com `full`: texto inteiro e thread
// aberta, sem os botões de expandir. O carrossel usa o XPostCard.

export const VerifiedBadge = ({ className = '' }) => (
  <svg viewBox="0 0 22 22" aria-label="Verificado" role="img" className={className} fill="currentColor">
    <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.265.14-1.898-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.291-.083-1.898.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.897-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.816.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
  </svg>
);

export const XLogo = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const PinIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M7 4.5C7 3.12 8.12 2 9.5 2h5C15.88 2 17 3.12 17 4.5v5.26L20.12 15H13v6l-1 2-1-2v-6H3.88L7 9.76z" />
  </svg>
);

const linkClass = 'text-x-blue hover:underline';

const PostText = ({ text, className = '' }) => (
  <div className={`whitespace-pre-wrap break-words ${className}`}>
    {tokenize(text).map((token, i) => {
      if (token.type === 'text') return <span key={i}>{token.value}</span>;

      const href =
        token.type === 'url'
          ? token.value
          : token.type === 'mention'
            ? `https://x.com/${token.value.slice(1)}`
            : `https://x.com/hashtag/${encodeURIComponent(token.value.slice(1))}`;

      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
          {token.type === 'url' ? displayUrl(token.value) : token.value}
        </a>
      );
    })}
  </div>
);

// Texto com corte de feed. Cada post da thread tem o seu próprio estado.
const PostBody = ({ text, full }) => {
  const [expanded, setExpanded] = useState(false);
  const short = full ? { text, truncated: false } : truncatePost(text);

  return (
    <div className="mt-1 text-[15px] leading-6 text-light-text">
      <PostText text={expanded || !short.truncated ? text : `${short.text}…`} />
      {short.truncated && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={`mt-1 ${linkClass}`}
        >
          {expanded ? 'Mostrar menos' : 'Mostrar mais'}
        </button>
      )}
    </div>
  );
};

// Grade de mídia com as proporções do X (1 a 4 itens). Clique abre a imagem inteira.
const MediaGrid = ({ media }) => {
  if (!media?.length) return null;

  const items = media.slice(0, 4);
  const single = items.length === 1;
  const style = single
    ? { aspectRatio: clampAspect(items[0].width, items[0].height) }
    : { aspectRatio: '16 / 9', gridTemplateRows: items.length > 2 ? '1fr 1fr' : '1fr' };

  return (
    <div
      className={`mt-3 grid gap-0.5 overflow-hidden rounded-2xl border border-white/10 ${single ? 'grid-cols-1' : 'grid-cols-2'}`}
      style={style}
    >
      {items.map((item, i) => (
        <a
          key={item.src}
          href={item.src}
          target="_blank"
          rel="noopener noreferrer"
          className={`relative block overflow-hidden bg-black ${items.length === 3 && i === 0 ? 'row-span-2' : ''}`}
        >
          <img
            src={item.src}
            alt={item.alt || 'Imagem do post'}
            width={item.width || undefined}
            height={item.height || undefined}
            loading="lazy"
            decoding="async"
            className={`h-full w-full ${single ? 'object-contain' : 'object-cover'}`}
          />
        </a>
      ))}
    </div>
  );
};

const QuoteCard = ({ quote }) => {
  if (!quote) return null;

  return (
    <a
      href={quote.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 block rounded-2xl border border-white/10 p-3 transition-colors hover:bg-white/[0.03]"
    >
      <div className="flex min-w-0 items-center gap-1 text-[15px] leading-5">
        {quote.author?.avatar && (
          <img src={quote.author.avatar} alt="" className="h-5 w-5 shrink-0 rounded-full" loading="lazy" />
        )}
        <span className="max-w-[60%] shrink-0 truncate font-bold text-light-text">{quote.author?.name}</span>
        <VerifiedBadge className="h-[18px] w-[18px] shrink-0 text-x-blue" />
        <span className="min-w-0 truncate text-secondary-text">@{quote.author?.handle}</span>
        <span className="text-secondary-text">·</span>
        <span className="shrink-0 text-secondary-text">{formatRelative(quote.publishedAt)}</span>
      </div>
      {/* colapsa parágrafos: o clamp conta linha em branco e a citação viraria
          três linhas de vazio */}
      <PostText text={quote.text.replace(/\n{2,}/g, '\n')} className="mt-1 line-clamp-3 text-[15px] leading-5 text-light-text" />
    </a>
  );
};

const PostRow = ({ author, post, lineBelow, full }) => (
  <div className="flex gap-3">
    <div className="flex shrink-0 flex-col items-center">
      <img src={author.avatar} alt="" className="h-10 w-10 rounded-full bg-dark-card" loading="lazy" />
      {/* /20 e não /15: o Tailwind 3.3 do projeto não tem 15 na escala de opacidade */}
      {lineBelow && <div className="mt-1 w-0.5 flex-1 bg-white/20" />}
    </div>

    <div className={`min-w-0 flex-1 ${lineBelow ? 'pb-5' : ''}`}>
      <div className="flex min-w-0 items-center gap-1 text-[15px] leading-5">
        {/* como no X: o @ corta antes do nome */}
        <span className="max-w-[60%] shrink-0 truncate font-bold text-light-text">{author.name}</span>
        {author.verified && <VerifiedBadge className="h-[18px] w-[18px] shrink-0 text-x-blue" />}
        <span className="min-w-0 truncate text-secondary-text">@{author.handle}</span>
        <span className="text-secondary-text">·</span>
        <a
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-secondary-text hover:underline"
          title={new Date(post.publishedAt).toLocaleString('pt-BR')}
        >
          {formatRelative(post.publishedAt)}
        </a>
        {/* no modal o rodapé já tem "Abrir no X", e o logo brigaria com o ✕ de fechar */}
        {!full && (
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Abrir no X"
            className="ml-auto hidden shrink-0 pl-2 text-secondary-text transition-colors hover:text-light-text sm:block"
          >
            <XLogo className="h-4 w-4" />
          </a>
        )}
      </div>

      <PostBody text={post.text} full={full} />
      <MediaGrid media={post.media} />
      <QuoteCard quote={post.quote} />
    </div>
  </div>
);

const XPost = ({ item, full = false }) => {
  const [threadOpen, setThreadOpen] = useState(full);
  const posts = threadOpen ? item.posts : item.posts.slice(0, 1);
  const hiddenCount = item.posts.length - 1;

  return (
    <article className="px-4 py-4 transition-colors hover:bg-white/[0.02] sm:px-5">
      {item.pinned && (
        <div className="mb-1 flex items-center gap-3 text-[13px] font-bold text-secondary-text">
          <span className="flex w-10 justify-end">
            <PinIcon className="h-4 w-4" />
          </span>
          Fixado
        </div>
      )}

      {posts.map((post, i) => (
        <PostRow key={post.id} author={item.author} post={post} lineBelow={i < posts.length - 1} full={full} />
      ))}

      {!full && item.isThread && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setThreadOpen((v) => !v)}
          aria-expanded={threadOpen}
          className={`mt-3 pl-[52px] text-[15px] ${linkClass}`}
        >
          {threadOpen ? 'Recolher thread' : `Mostrar esta thread (+${hiddenCount} posts)`}
        </button>
      )}
    </article>
  );
};

export default XPost;
